import React from 'react';
import clsx from 'clsx';
import { Button, Icon, IconChevronDown, Inline, Text } from '@treatwell/ui';
import { ConditionalWrapper } from 'js/components/Utilities/ConditionalWrapper';
import { capitalize } from 'js/helpers/capitalize';
import { Filter, FilterType, FilterWithOptionsType } from '../useFilters';
import styles from './FiltersButton.module.css';

type WithOptionalArgs<T, K extends keyof T> = Partial<Pick<T, K>> & Omit<T, K>;

const FiltersButtonDetails = ({
  hasChevron,
  count,
}: { hasChevron: boolean } & Pick<Filter<FilterWithOptionsType>, 'count'>) => {
  if (count) {
    return (
      <Text className={styles.count} type="caption">
        {count}
      </Text>
    );
  }

  if (hasChevron) {
    return <IconChevronDown size={16} />;
  }

  return null;
};

export const FiltersButton = ({
  chipLabel,
  id,
  hasOptions,
  isActive,
  count,
  icon,
  buttonColour: buttonStyle = 'default',
  dataCy,
  ref,
  onClick,
}: WithOptionalArgs<Filter<FilterType>, 'id' | 'hasOptions' | 'isActive'> & {
  count?: Filter<FilterWithOptionsType>['count'];
  icon?: Icon;
  buttonColour?: 'default' | 'red';
  dataCy?: string;
  ref?: React.Ref<HTMLButtonElement>;
  onClick: () => void;
}) => {
  const hasCount = count !== undefined;
  const hasChevron = (hasOptions ?? false) && id !== 'sortBy' && !hasCount;

  return (
    <Button
      className={clsx(
        styles.button,
        styles[`button${capitalize(buttonStyle)}`],
        {
          [styles.active]: isActive,
        }
      )}
      onClick={onClick}
      data-cy={dataCy}
      ref={ref}
    >
      <ConditionalWrapper
        condition={hasCount || hasChevron || icon !== undefined}
        render={(children) => <Inline space="xs">{children}</Inline>}
      >
        {icon ?? null}

        <Text type="caption">{chipLabel}</Text>
        <FiltersButtonDetails hasChevron={hasChevron} count={count} />
      </ConditionalWrapper>
    </Button>
  );
};
